import React, { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { api, API_BASE, tokenStore } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  FileText,
  Upload,
  Download,
  Trash2,
  CheckCircle2,
  CalendarRange,
  CalendarDays,
  ClipboardSignature,
  AlertCircle,
  Building2,
  CalendarCheck,
} from "lucide-react";
import { toast } from "sonner";
import { fmtDate, fmtDateTime, fileToBase64 } from "@/lib/utils-app";
import { StatTile, StatStrip } from "@/components/StatTile";

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function istMonthKey(d = new Date()) {
  const fmt = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
  });
  const p = Object.fromEntries(fmt.formatToParts(d).map((x) => [x.type, x.value]));
  return `${p.year}-${p.month}`;
}

function istToday() {
  const fmt = new Intl.DateTimeFormat("en-GB", { timeZone: "Asia/Kolkata", year: "numeric", month: "2-digit", day: "2-digit" });
  const p = Object.fromEntries(fmt.formatToParts(new Date()).map((x) => [x.type, x.value]));
  return new Date(`${p.year}-${p.month}-${p.day}T00:00:00`);
}

// Default "current upload month" = the previous IST month
function defaultUploadMonth() {
  const today = istToday();
  const prev = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const y = prev.getFullYear();
  const m = String(prev.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

function monthLabel(ym) {
  if (!ym) return "—";
  const [y, m] = ym.split("-");
  return `${MONTH_LABELS[parseInt(m, 10) - 1]} ${y}`;
}

// Compute upload window: due within 10 days of the next month
function ymStatus(ym) {
  if (!ym) return "future";
  const [y, m] = ym.split("-").map((s) => parseInt(s, 10));
  const today = istToday();
  const target = new Date(y, m - 1, 1);
  const dueBy = new Date(y, m, 10); // 10th of the next month
  if (target.getFullYear() > today.getFullYear() || (target.getFullYear() === today.getFullYear() && target.getMonth() >= today.getMonth())) {
    return "future"; // current or future month — not yet due
  }
  return today <= dueBy ? "due" : "overdue";
}

function StatusPill({ kind }) {
  if (kind === "done") return <Badge className="bg-emerald-600 hover:bg-emerald-600 text-white"><CheckCircle2 className="mr-1 h-3 w-3" />Uploaded</Badge>;
  if (kind === "due") return <Badge className="bg-amber-500 hover:bg-amber-500 text-white"><AlertCircle className="mr-1 h-3 w-3" />Due</Badge>;
  if (kind === "overdue") return <Badge className="bg-rose-600 hover:bg-rose-600 text-white"><AlertCircle className="mr-1 h-3 w-3" />Overdue</Badge>;
  return <Badge variant="outline" className="border-slate-300 text-muted-foreground">Not due</Badge>;
}

function downloadUrl(recId) {
  return `${API_BASE}/feedback/monthly/${recId}/download`;
}

async function triggerDownload(rec) {
  const url = downloadUrl(rec.id);
  try {
    const res = await fetch(url, { headers: { Authorization: `Bearer ${tokenStore.get()}` } });
    if (!res.ok) throw new Error("download failed");
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = rec.file_name || `feedback_${rec.year_month}_${rec.kind}`;
    a.click();
    URL.revokeObjectURL(a.href);
  } catch {
    toast.error("Download failed");
  }
}

// Open the document in a new tab/window (inline preview). Falls back to
// the browser default behaviour for the file's MIME type — PDFs and images
// render in-browser, other formats trigger download.
async function previewDocument(rec) {
  const url = `${API_BASE}/feedback/monthly/${rec.id}/download?inline=1`;
  // Optimistically open the new tab first to avoid the popup blocker; we'll
  // navigate it to the blob URL once the file is fetched.
  const win = window.open("", "_blank", "noopener,noreferrer");
  try {
    const res = await fetch(url, { headers: { Authorization: `Bearer ${tokenStore.get()}` } });
    if (!res.ok) throw new Error("preview failed");
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    if (win) {
      win.location.replace(objectUrl);
      // revoke after a delay so the new tab has time to read it
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    } else {
      // popup blocked → trigger a download as a fallback so the user
      // still gets the file
      const a = document.createElement("a");
      a.href = objectUrl;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      a.click();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    }
  } catch {
    if (win) win.close();
    toast.error("Unable to open document");
  }
}

function FormCell({ rec, onUpload, onDelete, canEdit, canUpload, blink }) {
  if (rec) {
    return (
      <div className="flex flex-col gap-1.5 rounded-md border bg-emerald-50/40 p-2" data-testid={`fb-cell-${rec.id}`}>
        <button
          type="button"
          onClick={() => previewDocument(rec)}
          className="group flex items-center gap-1.5 text-left rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
          title="Open document"
          data-testid={`fb-open-${rec.id}`}
        >
          <FileText className="h-3.5 w-3.5 text-emerald-700" />
          <span
            className="truncate text-[12px] font-medium text-emerald-800 underline decoration-emerald-300 underline-offset-2 group-hover:text-emerald-900 group-hover:decoration-emerald-600"
            title={rec.file_name}
          >{rec.file_name}</span>
        </button>
        <div className="text-[10px] text-muted-foreground">
          {(rec.file_size / 1024).toFixed(0)} KB · by {rec.uploaded_by_name} · {fmtDate(rec.created_at)}
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => triggerDownload(rec)} data-testid={`fb-dl-${rec.id}`}>
            <Download className="mr-1 h-3 w-3" /> Download
          </Button>
          {canUpload && (
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={onUpload} data-testid={`fb-replace-${rec.id}`}>
              Replace
            </Button>
          )}
          {canEdit && (
            <Button size="sm" variant="ghost" className="h-7 text-xs text-rose-600 hover:text-rose-700" onClick={onDelete} data-testid={`fb-del-${rec.id}`}>
              <Trash2 className="h-3 w-3" />
            </Button>
          )}
        </div>
      </div>
    );
  }
  if (!canUpload) {
    return (
      <div className="rounded-md border border-dashed bg-muted/30 p-2 text-center text-[11px] text-muted-foreground">
        Not yet uploaded
      </div>
    );
  }
  return (
    <Button
      size="sm"
      variant="outline"
      onClick={onUpload}
      className={`h-9 w-full rounded-md ${blink ? "animate-mild-blink border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100" : ""}`}
      data-testid="fb-upload-btn"
    >
      <Upload className="mr-1.5 h-4 w-4" /> Upload
    </Button>
  );
}

export default function FeedbackPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // Contractor Admin (read-only office login) and any other non-admin,
  // non-PMC role must never see the Upload control, regardless of month
  // status — only admin and ITCOT (PMC) supervisors are allowed to upload.
  const canUploadRole = isAdmin || user?.role === "pmc_supervisor";
  const [year, setYear] = useState(istToday().getFullYear());
  const [activeMonth, setActiveMonth] = useState(defaultUploadMonth());
  const [data, setData] = useState({ parks: [] });
  const [busy, setBusy] = useState(false);
  const [pickerCtx, setPickerCtx] = useState(null); // {park_id, kind}
  const fileInputRef = React.useRef(null);

  const load = () => {
    setBusy(true);
    api
      .get("/feedback/monthly/summary", { params: { year } })
      .then((r) => setData(r.data))
      .catch(() => setData({ parks: [] }))
      .finally(() => setBusy(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [year]);

  const months = useMemo(() => {
    const arr = [];
    for (let m = 1; m <= 12; m++) {
      arr.push(`${year}-${String(m).padStart(2, "0")}`);
    }
    return arr;
  }, [year]);

  const handleUpload = (park_id, kind) => {
    setPickerCtx({ park_id, kind });
    fileInputRef.current?.click();
  };

  const onFile = async (e) => {
    const f = e.target?.files?.[0];
    e.target.value = "";
    if (!f || !pickerCtx) return;
    const isPdf = f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf");
    if (!isPdf) {
      toast.error("Only PDF files (.pdf) are allowed");
      setPickerCtx(null);
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      toast.error("File too large (max 8 MB)");
      setPickerCtx(null);
      return;
    }
    try {
      const b64 = await fileToBase64(f);
      await api.post("/feedback/monthly", {
        park_id: pickerCtx.park_id,
        year_month: activeMonth,
        kind: pickerCtx.kind,
        file_b64: b64,
        file_name: f.name,
        file_mime: f.type || "application/octet-stream",
      });
      toast.success(
        isAdmin
          ? "Uploaded"
          : "Feedback form successfully submitted",
        isAdmin ? undefined : { description: "Admin can view it from the Feedback dashboard." },
      );
      load();
    } catch (er) {
      toast.error(er?.response?.data?.detail || "Upload failed");
    } finally {
      setPickerCtx(null);
    }
  };

  const removeRec = async (rec) => {
    const kindLabel = rec.kind === "po"
      ? "Project Officer"
      : rec.kind === "attendance" ? "Attendance" : "Association";
    if (!window.confirm(`Delete the ${kindLabel} form for ${rec.park_name} · ${monthLabel(rec.year_month)}?`)) return;
    try {
      await api.delete(`/feedback/monthly/${rec.id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  // Totals for the active month
  const monthTotals = (() => {
    let parks = 0, po = 0, assoc = 0, att = 0, overdue = 0;
    for (const p of data.parks) {
      parks += 1;
      const m = p.months?.[activeMonth] || {};
      if (m.po) po += 1;
      if (m.association) assoc += 1;
      if (m.attendance) att += 1;
    }
    if (ymStatus(activeMonth) === "overdue") {
      for (const p of data.parks) {
        const m = p.months?.[activeMonth] || {};
        if (!m.po) overdue += 1;
        if (!m.association) overdue += 1;
        if (!m.attendance) overdue += 1;
      }
    }
    return { parks, po, assoc, att, overdue };
  })();

  return (
    <AppShell
      title="Monthly Feedback"
      subtitle={
        isAdmin
          ? "Project Officer · Association · Attendance forms · due 10th of next month"
          : "Upload monthly PO · Association · Attendance forms · due 10th of next month"
      }
    >
      <div className="flex flex-col gap-3 sm:gap-4">
        <Card>
          <CardContent className="grid gap-3 p-3 sm:p-4 md:grid-cols-12">
            <div className="md:col-span-3 flex flex-col gap-1.5">
              <Label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                <CalendarRange className="mr-1 inline h-3 w-3" /> Year
              </Label>
              <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v, 10))}>
                <SelectTrigger data-testid="fb-year-select" className="h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(() => {
                    const cy = istToday().getFullYear();
                    const arr = [];
                    for (let y = cy + 1; y >= cy - 4; y--) arr.push(y);
                    return arr.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>);
                  })()}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-9 flex flex-col gap-1.5">
              <Label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                <CalendarDays className="mr-1 inline h-3 w-3" /> Active month
              </Label>
              <div className="grid grid-cols-6 gap-1 sm:flex sm:flex-wrap" data-testid="fb-month-strip">
                {months.map((m) => {
                  const isActive = m === activeMonth;
                  const status = ymStatus(m);
                  const tone = status === "future" ? "opacity-50" : "";
                  return (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setActiveMonth(m)}
                      className={`rounded-md border px-2 py-1.5 text-xs font-medium transition-colors sm:px-3 ${tone} ${
                        isActive
                          ? "border-primary bg-primary text-primary-foreground"
                          : "bg-background hover:bg-secondary"
                      }`}
                      data-testid={`fb-month-${m}`}
                    >
                      {MONTH_LABELS[parseInt(m.split("-")[1], 10) - 1]}
                    </button>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>

        <StatStrip cols={isAdmin ? 5 : 3}>
          {isAdmin && (
            <StatTile icon={Building2} accent="slate" label="Parks" value={monthTotals.parks} hint={monthLabel(activeMonth)} testid="fb-stat-parks" />
          )}
          <StatTile icon={ClipboardSignature} accent="sky" label="PO Forms" value={`${monthTotals.po}/${monthTotals.parks}`} testid="fb-stat-po" />
          <StatTile icon={FileText} accent="violet" label="Association" value={`${monthTotals.assoc}/${monthTotals.parks}`} testid="fb-stat-assoc" />
          <StatTile icon={CalendarCheck} accent="emerald" label="Attendance" value={`${monthTotals.att}/${monthTotals.parks}`} testid="fb-stat-att" />
          {isAdmin && (
            <StatTile icon={AlertCircle} accent={ymStatus(activeMonth) === "overdue" ? "rose" : "amber"} label="Status" value={ymStatus(activeMonth) === "overdue" ? "Overdue" : ymStatus(activeMonth) === "due" ? "Due" : "Future"} hint={ymStatus(activeMonth) === "overdue" ? `${monthTotals.overdue} missing` : "Due 10th of next month"} testid="fb-stat-status" />
          )}
        </StatStrip>

        {busy ? (
          <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">Loading…</CardContent></Card>
        ) : data.parks.length === 0 ? (
          <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">No parks available.</CardContent></Card>
        ) : (
          <>
            {/* Mobile: stacked cards per park */}
            <div className="space-y-2 md:hidden">
              {data.parks.map((p) => {
                const m = p.months?.[activeMonth] || {};
                const po = m.po || null;
                const assoc = m.association || null;
                const att = m.attendance || null;
                const canUpload = canUploadRole && ymStatus(activeMonth) !== "future";
                const shouldBlink = canUpload && (ymStatus(activeMonth) === "due" || ymStatus(activeMonth) === "overdue");
                const cellStatus =
                  po && assoc && att ? "done"
                    : ymStatus(activeMonth) === "overdue" ? "overdue"
                    : ymStatus(activeMonth) === "due" ? "due"
                    : "future";
                return (
                  <Card key={p.park_id} data-testid={`fb-row-${p.park_code}`}>
                    <CardContent className="p-3 space-y-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-medium truncate">{p.park_name}</div>
                          <div className="text-[11px] font-mono text-muted-foreground">{p.park_code}</div>
                        </div>
                        <StatusPill kind={cellStatus} />
                      </div>
                      <div className="space-y-2">
                        <div>
                          <div className="mb-1 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                            <ClipboardSignature className="h-3 w-3" /> Project Officer Form
                          </div>
                          <FormCell
                            rec={po}
                            onUpload={() => handleUpload(p.park_id, "po")}
                            onDelete={() => removeRec(po)}
                            canEdit={isAdmin}
                            canUpload={canUpload}
                            blink={shouldBlink && !po}
                          />
                        </div>
                        <div>
                          <div className="mb-1 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                            <FileText className="h-3 w-3" /> Association Form
                          </div>
                          <FormCell
                            rec={assoc}
                            onUpload={() => handleUpload(p.park_id, "association")}
                            onDelete={() => removeRec(assoc)}
                            canEdit={isAdmin}
                            canUpload={canUpload}
                            blink={shouldBlink && !assoc}
                          />
                        </div>
                        <div>
                          <div className="mb-1 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                            <CalendarCheck className="h-3 w-3" /> Attendance Form
                          </div>
                          <FormCell
                            rec={att}
                            onUpload={() => handleUpload(p.park_id, "attendance")}
                            onDelete={() => removeRec(att)}
                            canEdit={isAdmin}
                            canUpload={canUpload}
                            blink={shouldBlink && !att}
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Desktop / tablet: table */}
            <Card className="hidden md:block">
              <CardContent className="p-0 overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="px-3 py-2 text-left font-medium" style={{ minWidth: 220 }}>Park</th>
                      <th className="px-3 py-2 text-left font-medium" style={{ minWidth: 240 }}>
                        <ClipboardSignature className="mr-1 inline h-4 w-4" />
                        Project Officer Form
                      </th>
                      <th className="px-3 py-2 text-left font-medium" style={{ minWidth: 240 }}>
                        <FileText className="mr-1 inline h-4 w-4" />
                        Association Form
                      </th>
                      <th className="px-3 py-2 text-left font-medium" style={{ minWidth: 240 }}>
                        <CalendarCheck className="mr-1 inline h-4 w-4" />
                        Attendance Form
                      </th>
                      <th className="px-3 py-2 text-left font-medium" style={{ minWidth: 110 }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.parks.map((p) => {
                      const m = p.months?.[activeMonth] || {};
                      const po = m.po || null;
                      const assoc = m.association || null;
                      const att = m.attendance || null;
                      const canUpload = canUploadRole && ymStatus(activeMonth) !== "future";
                      const shouldBlink = canUpload && (ymStatus(activeMonth) === "due" || ymStatus(activeMonth) === "overdue");
                      const cellStatus =
                        po && assoc && att ? "done"
                          : ymStatus(activeMonth) === "overdue" ? "overdue"
                          : ymStatus(activeMonth) === "due" ? "due"
                          : "future";
                      return (
                        <tr key={p.park_id} className="border-t align-top" data-testid={`fb-row-${p.park_code}-tab`}>
                          <td className="px-3 py-2">
                            <div className="font-medium">{p.park_name}</div>
                            <div className="text-[11px] font-mono text-muted-foreground">{p.park_code}</div>
                          </td>
                          <td className="px-3 py-2">
                            <FormCell
                              rec={po}
                              onUpload={() => handleUpload(p.park_id, "po")}
                              onDelete={() => removeRec(po)}
                              canEdit={isAdmin}
                              canUpload={canUpload}
                              blink={shouldBlink && !po}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <FormCell
                              rec={assoc}
                              onUpload={() => handleUpload(p.park_id, "association")}
                              onDelete={() => removeRec(assoc)}
                              canEdit={isAdmin}
                              canUpload={canUpload}
                              blink={shouldBlink && !assoc}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <FormCell
                              rec={att}
                              onUpload={() => handleUpload(p.park_id, "attendance")}
                              onDelete={() => removeRec(att)}
                              canEdit={isAdmin}
                              canUpload={canUpload}
                              blink={shouldBlink && !att}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <StatusPill kind={cellStatus} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </>
        )}

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
          <span><AlertCircle className="mr-1 inline h-3 w-3 text-amber-500" /> Forms due by 10th of next month.</span>
          <span><CheckCircle2 className="mr-1 inline h-3 w-3 text-emerald-600" /> Only PDF files (.pdf) allowed. Uploads can be replaced anytime.</span>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf,.pdf"
        className="hidden"
        onChange={onFile}
        data-testid="fb-file-input"
      />
    </AppShell>
  );
}
